// =================================================================================
// File: src/pages/visitor/BookStudioSpacePage.jsx
// Description: Visitor-facing studio space booking with liquid glass UI
// Fetches published spaces from public API (server-side cached)
// =================================================================================

import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, DollarSign, Filter, Search, Calendar, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch } from "../../lib/apiFetch";
import VisitorSpaceBookingForm from "../../components/Modals/VisitorSpaceBookingForm";
import VisitorSlotPicker from "../../components/Modals/VisitorSlotPicker";
import { getStudioLogoUrl } from "../../lib/studioLogo";
import { createImageHandlers } from "../../lib/imageCache";

/**
 * BookStudioSpacePage - Visitor World gallery view
 * 
 * Features:
 * - Liquid glass card styling matching Mudo theme
 * - Real-time space data from StudioSpaceContext
 * - Filter by tags (equipment, availability)
 * - Click to book with SpaceBookingModal
 */
export default function BookStudioSpacePage() {
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const location = useLocation();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  const didFetchRef = useRef(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    console.log('[BookStudioSpacePage] route enter', { spaceId, state: location.state });
  }, [spaceId, location.state]);

  // Fetch published spaces; if a spaceId param is present, fetch only that space
  useEffect(() => {
    // Prevent duplicate fetchSpaces calls in DEV due to StrictMode effect double-invoke
    if (import.meta.env.DEV && didFetchRef.current) return;
    didFetchRef.current = true;

    async function fetchSpaces() {
      try {
        setLoading(true);
        if (spaceId) {
          console.log('[BookStudioSpacePage] Fetching single space for visitor route', spaceId);
          const res = await apiFetch(
            `/api/public/space/${encodeURIComponent(String(spaceId))}`
          );
          const json = await res.json();
          if (!res.ok) {
            console.error('[BookStudioSpacePage] Error fetching space by id:', json);
            setSpaces([]);
            return;
          }
          const rows = Array.isArray(json) ? json : json?.data;
          const data = Array.isArray(rows) ? rows : [];

          // If space was passed via navigation state, merge it for display richness
          const stateSpace = location.state?.space;
          const stateStudio = location.state?.studio;

          // Preserve the fetched row, but enrich with any navigation state (space or studio)
          const mergedSpace = data?.[0]
            ? { ...data[0], ...stateSpace }
            : stateSpace
            ? { ...stateSpace }
            : null;

          if (!mergedSpace) {
            setSpaces([]);
            return;
          }

          // Attach studio from navigation state if present.
          if (!mergedSpace.studio && stateStudio) mergedSpace.studio = stateStudio;

          const merged = [mergedSpace];

          console.log('[BookStudioSpacePage] Loaded space by id:', merged);
          setSpaces(merged);
          return;
        }

        // No param: fetch all published spaces via public API (CDN cached)
        const res = await apiFetch('/api/public/spaces');
        const json = await res.json();
        if (!res.ok) {
          console.error('[BookStudioSpacePage] Error fetching spaces:', json);
          setSpaces([]);
          return;
        }
        const rows = Array.isArray(json) ? json : json?.data;
        const data = Array.isArray(rows) ? rows : [];
        console.log('[BookStudioSpacePage] Fetched spaces:', data);
        setSpaces(data);
      } catch (err) {
        console.error('[BookStudioSpacePage] Error:', err);
        setSpaces([]);
      } finally {
        setLoading(false);
      }
    }

    fetchSpaces();
  }, [spaceId, location.state]);

  // Extract all unique tags from spaces
  const allTags = useMemo(() => {
    const tagSet = new Set();
    spaces.forEach(space => {
      if (Array.isArray(space.tags)) {
        space.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet);
  }, [spaces]);

  // Filter spaces
  const filteredSpaces = useMemo(() => {
    const filtered = spaces.filter((space) => {
      // Only show active spaces to visitors
      if (space.is_active === false) return false;
      
      const matchesSearch = 
        space.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        space.location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (selectedFilter === "all") return matchesSearch;
      if (selectedFilter === "available-today") {
        return matchesSearch && space.availability?.statusBadge?.toLowerCase().includes("today");
      }
      
      // Filter by tag
      return matchesSearch && space.tags?.includes(selectedFilter);
    });
    return filtered;
  }, [spaces, searchQuery, selectedFilter]);

  const handleBookSpace = (space) => {
    if (!space?.id) {
      console.warn('[BookStudioSpacePage] Missing space id, cannot navigate');
      return;
    }
    console.log('[BookStudioSpacePage] CTA clicked:', { spaceId: space?.id, spaceName: space?.name });
    
    // Open slot picker first (required for workshop pattern)
    setSelectedSpace(space);
    setShowSlotPicker(true);
  };

  const handleSlotSelected = (slots) => {
    const normalized = Array.isArray(slots) ? slots : (slots ? [slots] : []);
    console.log('[BookStudioSpacePage] Slots selected from picker:', {
      count: normalized.length,
      slotIds: normalized.map((s) => s?.id).filter(Boolean),
      spaceId: selectedSpace?.id,
    });

    setSelectedSlots(normalized);
    setShowSlotPicker(false);
    setShowBookingForm(true);
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
    setSelectedSlots([]);
    setSelectedSpace(null);
  };

  return (
    <div className="relative w-full min-h-screen overflow-hidden font-sans text-white">
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="fixed top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src="/intro.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="fixed inset-0 z-10 bg-black/40" />

      {/* Content */}
      <div className="relative z-20 min-h-screen">
        {/* Header */}
        <motion.div
          className="sticky top-0 z-30 bg-black/30 backdrop-blur-md border-b border-white/10"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => navigate("/discover")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back</span>
            </button>
            <h1 className="text-2xl font-bold">Book Studio Space</h1>
            <div className="w-24" />
          </div>
        </motion.div>

        {/* Single Space Booking Mode - Show when spaceId param present */}
        {spaceId && spaces.length > 0 && !showSlotPicker && !showBookingForm ? (
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="mudo-glass-panel p-8 text-center">
              <p className="text-white/70 mb-4">Loading space details...</p>
              <button
                onClick={() => {
                  const space = spaces[0];
                  setSelectedSpace(space);
                  setShowSlotPicker(true);
                }}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-all"
              >
                View Available Slots
              </button>
            </div>
          </div>
        ) : spaceId && loading ? (
          <div className="max-w-7xl mx-auto px-6 py-12 text-center">
            <div className="inline-block w-10 h-10 border-4 border-white/40 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-white/70">Loading space details…</p>
          </div>
        ) : (
          /* Gallery Mode - Show when no spaceId param */
          <motion.div
            className="max-w-7xl mx-auto px-6 py-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Search Bar */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Search by studio name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
            </div>

            {/* Filter Pills (Liquid Glass Style) */}
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              <div className="flex items-center gap-2 text-white/70">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <button
                onClick={() => setSelectedFilter("all")}
                className={`mudo-glass-pill px-4 py-2 text-sm font-medium transition-all ${
                  selectedFilter === "all"
                    ? "ring-2 ring-white/40"
                    : "hover:bg-white/20"
                }`}
                style={{
                  background: selectedFilter === "all" 
                    ? "var(--color-primary, linear-gradient(135deg, rgba(255,255,255,0.20), rgba(255,255,255,0.08)))"
                    : undefined
                }}
              >
                All Spaces
              </button>
              <button
                onClick={() => setSelectedFilter("available-today")}
                className={`mudo-glass-pill px-4 py-2 text-sm font-medium transition-all ${
                  selectedFilter === "available-today"
                    ? "ring-2 ring-white/40"
                    : "hover:bg-white/20"
                }`}
                style={{
                  background: selectedFilter === "available-today" 
                    ? "var(--color-primary, linear-gradient(135deg, rgba(255,255,255,0.20), rgba(255,255,255,0.08)))"
                    : undefined
                }}
              >
                Available Today
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedFilter(tag)}
                  className={`mudo-glass-pill px-4 py-2 text-sm font-medium transition-all ${
                    selectedFilter === tag
                      ? "ring-2 ring-white/40"
                      : "hover:bg-white/20"
                  }`}
                  style={{
                    background: selectedFilter === tag 
                      ? "var(--color-primary, linear-gradient(135deg, rgba(255,255,255,0.20), rgba(255,255,255,0.08)))"
                      : undefined
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <p className="text-white/70">
                {filteredSpaces.length} studio space{filteredSpaces.length !== 1 ? "s" : ""} available
              </p>
            </div>

            {/* Spaces Grid with Liquid Glass Cards */}
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-12"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.1 } }
              }}
            >
              <AnimatePresence mode="popLayout">
                {filteredSpaces.map((space) => (
                  <SpaceCard
                    key={space.id}
                    space={space}
                    bookings={[]}
                    onBook={() => handleBookSpace(space)}
                  />
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Empty State */}
            {filteredSpaces.length === 0 && (
              <motion.div
                className="mudo-glass-panel p-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-6xl mb-4">🏺</div>
                <h3 className="text-xl font-semibold mb-2">No spaces found</h3>
                <p className="text-white/60">Try adjusting your search or filters</p>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>

      {/* Slot Picker Modal */}
      {showSlotPicker && selectedSpace && (
        <VisitorSlotPicker
          space={selectedSpace}
          onClose={() => {
            setShowSlotPicker(false);
            setSelectedSpace(null);
          }}
          onSlotSelected={handleSlotSelected}
        />
      )}

      {/* Booking Form Modal */}
      {showBookingForm && selectedSpace && (
        <VisitorSpaceBookingForm
          space={selectedSpace}
          slots={selectedSlots}
          onClose={handleCloseBookingForm}
        />
      )}
    </div>
  );
}

// ==========================
// SpaceCard Component
// ==========================
function getSeatsTotal(space) {
  const raw =
    space?.seats_total ??
    space?.seatsTotal ??
    space?.capacity?.seats ??
    space?.capacity_seats ??
    space?.seats ??
    1;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.floor(parsed);
  }
  return 1;
}

const SpaceCard = React.forwardRef(({ space, onBook, bookings = [] }, ref) => {
  const {
    name,
    location,
    description,
    tags = [],
    pricing = {},
    heroImageUrl,
    accentColor,
    id: spaceId,
    studio,
    studio_id,
  } = space;
  const seatsTotal = getSeatsTotal(space);
  const studioFetchErrors = React.useRef(new Set());
  console.log('[SpaceCard] space:', { spaceId, studio_id, studio });
  
  // Studio data is expected to be attached by the API (space.studio)
  const [studioData, setStudioData] = React.useState(studio || null);
  
  React.useEffect(() => {
    if (studio) setStudioData(studio);
  }, [studio, studio_id]);

  const studioLogoUrl = useMemo(() => getStudioLogoUrl(studioData), [studioData?.id, studioData?.settings?.customize_v1?.logoUrl, studioData?.logo_url, studioData?.logoUrl, studioData?.image_url, studioData?.avatar_url]);
  const imageHandlers = useMemo(() => createImageHandlers(studioLogoUrl), [studioLogoUrl]);

  if (import.meta.env.DEV) {
    console.log("[visitor][SpaceCard]", {
      spaceId,
      name,
      seats_total: space.seats_total,
      capacitySeats: space.capacity?.seats,
      derived: seatsTotal,
    });
  }

  // Calculate available spots from bookings
  const availabilityInfo = useMemo(() => {
    const maxSpots = seatsTotal;

    // Count confirmed bookings for this space
    const bookedCount = (bookings || []).reduce((sum, b) => {
      const matchesSpace = (b.spaceId || b.space_id) === spaceId;
      const status = (b.paymentStatus || b.payment_status || "").toLowerCase();
      const isPaidOrCompleted = status === "paid" || status === "completed";
      if (matchesSpace && isPaidOrCompleted) {
        return sum + (Number.isFinite(Number(b.seats)) ? Number(b.seats) : 1);
      }
      return sum;
    }, 0);

    const available = Math.max(0, maxSpots - bookedCount);
    
    return {
      max: maxSpots,
      available,
      booked: bookedCount,
      isAvailable: available > 0,
      isFull: available === 0,
    };
  }, [bookings, seatsTotal, spaceId]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="mudo-glass-panel mudo-glass-interactive overflow-hidden group"
    >
      {/* Top gradient banner with clock icon */}
      <div 
        className="h-2 w-full"
        style={{
          background: accentColor || "var(--color-primary, linear-gradient(90deg, #f97316, #fb923c))",
        }}
      />
      
      {/* Card Content */}
      <div className="p-6">
        {/* Studio Header - Logo + Name */}
        {/* Studio Header - Logo + Name - Show if we have studio data */}
        {studioData ? (
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
            {studioLogoUrl && (
              <img 
                src={studioLogoUrl} 
                alt={studioData.name}
                className="h-10 w-auto rounded-lg object-contain bg-white/10 px-2"
                loading="lazy"
                decoding="async"
                width={40}
                onLoad={imageHandlers.onLoad}
                onError={imageHandlers.onError}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/60 uppercase tracking-wider">Hosted by</p>
              <p className="text-sm font-semibold text-white truncate">{studioData.name}</p>
            </div>
            {/* Availability Badge */}
            <div className={`mudo-glass-pill px-3 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
              availabilityInfo.isFull 
                ? 'bg-red-500/20 text-red-200 border border-red-500/30' 
                : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'
            }`}>
              {availabilityInfo.available} of {availabilityInfo.max} available
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
            <div className="flex items-center gap-2 text-white/50">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Studio Space</span>
            </div>
            {/* Availability Badge */}
            <div className={`mudo-glass-pill px-3 py-1 text-xs font-medium rounded-full ${
              availabilityInfo.isFull 
                ? 'bg-red-500/20 text-red-200 border border-red-500/30' 
                : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'
            }`}>
              {availabilityInfo.available} of {availabilityInfo.max} available
            </div>
          </div>
        )}

        {/* Title & Location */}
        <h3 className="text-2xl font-bold mb-2 group-hover:text-white/90 transition-colors">
          {name}
        </h3>
        <div className="flex items-center gap-2 text-white/60 mb-4">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{location}</span>
        </div>

        {/* Description */}
        <p className="text-white/70 text-sm mb-4 line-clamp-2">
          {description}
        </p>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.slice(0, 4).map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/80"
              >
                {tag}
              </span>
            ))}
            {tags.length > 4 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/60">
                +{tags.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Capacity */}
        {seatsTotal > 0 && (
          <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
            <Users className="w-4 h-4" />
            <span>Up to {seatsTotal} people</span>
          </div>
        )}

        {/* Pricing Schedule - Full Display */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 text-white/50" />
            <h4 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Pricing</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Hourly Rate */}
            {(pricing.hourly ?? pricing.perHour) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Hourly</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.hourly ?? pricing.perHour}
                  <span className="text-xs text-white/60 font-normal">/hr</span>
                </p>
              </div>
            )}
            
            {/* Daily Rate */}
            {(pricing.daily ?? pricing.perDay) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Daily</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.daily ?? pricing.perDay}
                  <span className="text-xs text-white/60 font-normal">/day</span>
                </p>
              </div>
            )}
            
            {/* Weekly Rate */}
            {(pricing.weekly ?? pricing.perWeek) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Weekly</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.weekly ?? pricing.perWeek}
                  <span className="text-xs text-white/60 font-normal">/wk</span>
                </p>
              </div>
            )}
            
            {/* Monthly Rate */}
            {(pricing.monthly ?? pricing.perMonth) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Monthly</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.monthly ?? pricing.perMonth}
                  <span className="text-xs text-white/60 font-normal">/mo</span>
                </p>
              </div>
            )}
            
            {/* Per KG Rate */}
            {(pricing.perKg ?? pricing.per_kg) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Per Kilogram</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.perKg ?? pricing.per_kg}
                  <span className="text-xs text-white/60 font-normal">/kg</span>
                </p>
              </div>
            )}
            
            {/* Per Pound Rate */}
            {(pricing.perLb ?? pricing.per_lb) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Per Pound</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.perLb ?? pricing.per_lb}
                  <span className="text-xs text-white/60 font-normal">/lb</span>
                </p>
              </div>
            )}
            
            {/* Per Firing Rate */}
            {(pricing.perFiring ?? pricing.per_firing) && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <p className="text-xs text-white/50 mb-1">Per Firing</p>
                <p className="text-lg font-bold text-white">
                  ${pricing.perFiring ?? pricing.per_firing}
                  <span className="text-xs text-white/60 font-normal">/fire</span>
                </p>
              </div>
            )}
            
            {/* Custom/Other Rates */}
            {pricing.custom && (
              <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 col-span-2">
                <p className="text-xs text-white/50 mb-1">Custom Rate</p>
                <p className="text-sm text-white/80">{pricing.custom}</p>
              </div>
            )}
          </div>
          
          {/* No Pricing Available Message */}
          {!pricing.hourly && !pricing.perHour && !pricing.daily && !pricing.perDay && 
           !pricing.weekly && !pricing.perWeek && !pricing.monthly && !pricing.perMonth &&
           !pricing.perKg && !pricing.per_kg && !pricing.perLb && !pricing.per_lb &&
           !pricing.perFiring && !pricing.per_firing && !pricing.custom && (
            <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-sm text-white/60">Contact studio for pricing</p>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <button
          onClick={() => {
            console.log('[SpaceCard] CTA button clicked:', { spaceId, name });
            onBook(space);
          }}
          disabled={availabilityInfo.isFull}
          className={`w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 ${
            availabilityInfo.isFull
              ? 'bg-gray-600 cursor-not-allowed opacity-60'
              : 'hover:scale-[1.02] hover:shadow-xl'
          }`}
          style={{
            background: availabilityInfo.isFull ? undefined : (accentColor || "var(--color-primary, linear-gradient(135deg, #f97316, #fb923c))"),
          }}
        >
          {availabilityInfo.isFull ? 'Fully Booked' : 'View Availability & Book'}
        </button>
      </div>
    </motion.div>
  );
});
